/**
 * One task in the register, and the tracks the whole list is laid out on.
 *
 * Split out of `TasksList` when the register grew its assignee column: the list
 * file was already holding the query, the paging, the two views and the row, and
 * the row is the part that grows every time somebody has a new question about a
 * task.
 *
 * `COLS` lives HERE, with the row that draws the cells, and the head imports it.
 * Two track lists drift apart the first time a column is added and then the
 * values sit under the wrong heading - the same reason `ExpenseRow` owns its
 * own. The head is only labels; the row is the thing that has to line up.
 */
import { Link } from "react-router-dom";
import { CalendarClock, CheckSquare, EyeOff, FolderKanban, MessageSquare } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import type { Task, TaskStatus } from "../../../lib/projects";
import { StatusPill, fmtDate, fmtNum } from "../../ui";
import { UserAvatars } from "../_shared/UserChips";
import { isOverdue } from "../projects/lib";
import { taskTone } from "../projects/status";
import { TimerMark } from "../../timer";
import { TaskRowMenu } from "./TaskRowMenu";
import { priorityIsNotable, priorityTone } from "./priority";

/**
 * Task, status, priority, assignees, due date, comments - six tracks plus the
 * actions column, used by both the head and the rows so a new column cannot
 * shift one without the other.
 *
 * The title gave up the width the faces took (2.6fr to 2.3fr) rather than the
 * row getting wider: `listCard` derives the card's scroll floor from these
 * tracks, so every column added moves the point at which a laptop starts
 * scrolling sideways. The assignee track is the narrowest flexible one because
 * it holds discs of a fixed size - four faces and a tail are 100px - and needs
 * no room to grow.
 */
export const COLS = "minmax(0,2.3fr) 1fr .9fr .8fr 1fr .55fr 44px";

/**
 * How many faces before the stack turns into "+N".
 *
 * Four, which draws up to five before it counts (see `UserAvatars`): five discs
 * are 100px and the track's own floor is 130px, so the cap is set by the width
 * that is actually there rather than by a round number.
 */
const MAX_FACES = 4;

/**
 * How many tags before the rest become "+N".
 *
 * Three, chosen against the track and not as a round number: the title column is
 * 2.3fr and already carries the project name on the same line, and a fourth chip
 * is where the name it sits beside starts losing characters to the ellipsis.
 */
const MAX_TAGS = 3;

export function TaskRow({
  task,
  onMove,
  onDelete,
}: {
  task: Task;
  onMove: (s: TaskStatus) => void;
  onDelete: () => void;
}) {
  const t = useT();
  const late = isOverdue(task);
  const comments = task._count?.comments ?? 0;
  // Absent on a task that did not come from the LIST endpoint - the detail sends
  // the items themselves instead. Undefined therefore means "not counted here"
  // and is drawn as nothing, while `{ done: 0, total: 0 }` is a real answer: the
  // task has no checklist. Only the second is a statement, and neither is a
  // reason to draw a placeholder.
  const checklist = task.checklistProgress;

  return (
    // A div, not a link: the row ends in a menu button, and interactive content
    // inside an <a> is invalid HTML and swallows the button's clicks. The title
    // link stretches an ::after over the whole row instead, so the row stays
    // clickable end to end and the button still sits on top of it.
    <div className="crm-list-row relative" style={{ gridTemplateColumns: COLS }}>
      {/* No avatar beside the title: a task has no name to make initials from,
          and the project it belongs to is the useful second line instead. */}
      <span className="min-w-0 pl-3">
        <span className="flex min-w-0 items-center gap-1.5">
          <Link
            to={`/tasks/${task.id}`}
            className={cn(
              "crm-prow-n crm-trunc block after:absolute after:inset-0 after:rounded-full",
              task.status === "done" && "crm-mut line-through",
            )}
          >
            {task.title}
          </Link>

          {/* The same closed eye `ProjectTasks` draws, for the same reasons: no
              column of its own (almost every row is visible, so the column would
              spend a sixth of the width repeating "yes"), the tooltip on a
              wrapper because an `<svg>` ignores `title`, and `relative z-[1]` so
              it sits above the link's row-wide overlay instead of under it.
              This list is the screen an operator scans daily, so a task hidden
              from the customer has to be readable as hidden here too. */}
          {task.visibleToClient ? null : (
            <span
              className="crm-mut relative z-[1] inline-flex shrink-0"
              role="img"
              title={t("projects.hiddenFromClient")}
              aria-label={t("projects.hiddenFromClient")}
            >
              <EyeOff className="size-3.5" />
            </span>
          )}

          {/* A clock running on this row, anybody's. No column of its own, for
              the same reason the eye beside it has none: almost every row would
              spend the width saying "nobody is on this". `relative z-[1]` puts
              it above the title link's row-wide overlay, so its tooltip is
              reachable instead of being swallowed by the click target. */}
          <TimerMark taskId={task.id} />
        </span>
        {/* The second line is where a task says what it is FILED under: the
            project, then the labels, then how far the plan for it has got. None
            of the three gets a column of its own - the row already carries six
            tracks, and `listCard` derives the card's sideways-scroll floor from
            them, so a seventh would move the width at which a laptop starts
            scrolling for the sake of a cell that is empty on most rows.

            `min-w-0` and `crm-trunc` on the project name only: it is the part
            that may be a sentence, so it is the part that gives way. The tags
            and the count are short and fixed, and shrinking them would turn a
            label into an ellipsis that says nothing. */}
        <span className="crm-prow-s flex min-w-0 items-center gap-1.5">
          <span className="crm-trunc flex min-w-0 items-center gap-1.5">
            {task.project ? (
              <>
                <FolderKanban className="size-3 shrink-0" />
                {task.project.name}
              </>
            ) : (
              t("tasks.noProject")
            )}
          </span>

          {/* Drawn here rather than in a cell so the filter above the list has
              something to check itself against. Capped at three: the register is
              a page somebody scans, and a task wearing eight labels would push
              its own project name off the row. The rest are counted rather than
              hidden - "+2" is a fact, a silently shorter list is not. */}
          {task.tags.slice(0, MAX_TAGS).map((tag) => (
            <span key={tag} className="crm-chip shrink-0">
              {tag}
            </span>
          ))}
          {task.tags.length > MAX_TAGS ? (
            <span className="crm-tnum shrink-0">+{fmtNum(task.tags.length - MAX_TAGS)}</span>
          ) : null}

          {/* The checklist, as the count the list endpoint computes for the whole
              page in one query. Only where there are boxes: "0/0" on every task
              nobody has broken up would be a figure about nothing on most rows.

              It says how much of the PLAN is ticked and nothing about the task -
              a full checklist does not complete a task, which is why this is a
              quiet pair of numbers and not a state a reader could take for one.
              The tooltip names the subject, since two numbers with a slash could
              be anything. */}
          {checklist && checklist.total > 0 ? (
            <span
              className="crm-tnum relative z-[1] flex shrink-0 items-center gap-1"
              title={t("tasks.checklist")}
            >
              <CheckSquare className="size-3 shrink-0" />
              {checklist.done}/{checklist.total}
            </span>
          ) : null}
        </span>
      </span>

      <span>
        <StatusPill tone={taskTone(task.status)}>{t(`tasks.status.${task.status}`)}</StatusPill>
      </span>

      {/* Only a priority somebody chose gets a pill - see `priority.ts`. The
          default reads as plain text so the colour on the row above means
          something. */}
      <span>
        {priorityIsNotable(task.priority) ? (
          <StatusPill tone={priorityTone(task.priority)}>{t(`tasks.prio.${task.priority}`)}</StatusPill>
        ) : (
          <span className="crm-mut text-[12.5px]">{t(`tasks.prio.${task.priority}`)}</span>
        )}
      </span>

      {/* WHO, at last. `Task.assignees` has been writable since the module was
          written and no list ever drew it, so an operator could put four people
          on a task and then find no screen that said so.

          A cell with nobody on it draws the directory's "-" and not a word.
          Today that is EVERY row - the Perfex import found one host user and
          refused to invent owners, so the whole column is dashes on day one -
          and the temptation is to write "Unassigned" instead so the emptiness
          reads as a fact rather than a failure. It stays a dash for two reasons:
          the comments column beside it already means "none" by exactly this
          mark, on rows where it is also the common answer, and `UserChips` owns
          the two marks precisely so that "nobody is on this" cannot come out as
          one thing here and another on the milestone next door. A column of
          quiet dashes with the head above it saying who they are for is what an
          unstaffed backlog honestly looks like; the first face assigned then
          stands out against them, which is the whole point of the column.

          `relative z-[1]` on the stack only, the same trick the closed eye
          above uses: the title link covers the row with an ::after, and a
          `title` under that overlay never opens - which would leave a reader
          with initials and no way to turn them into names. The wrapper is
          inline-flex so the dead spot is the discs and not the whole track. */}
      <span className="crm-mut flex min-w-0 items-center text-[13px]">
        <span className="relative z-[1] inline-flex">
          <UserAvatars ids={task.assignees} max={MAX_FACES} size={28} />
        </span>
      </span>

      <span className={cn("crm-tnum flex items-center gap-1.5 text-[12.5px]", late ? "text-red-600" : "crm-mut")}>
        <CalendarClock className="size-3.5 shrink-0" />
        {fmtDate(task.dueDate)}
      </span>

      <span className="crm-mut crm-tnum flex items-center gap-1.5 text-[13px]">
        {comments > 0 ? (
          <>
            <MessageSquare className="size-3.5 shrink-0" />
            {fmtNum(comments)}
          </>
        ) : (
          "-"
        )}
      </span>

      <TaskRowMenu task={task} onMove={onMove} onDelete={onDelete} />
    </div>
  );
}
